/**
 * bossArena.ts
 * Boss-arena helpers shared by the physics step and the scene layer.
 *
 * A "boss arena" is any level whose `gameState.enemies` contains an
 * entity tagged with one of the boss `entityType` strings below. The
 * physics step uses {@link getActiveBoss} to detect boss death so it
 * can set `gameState.levelComplete = true` exactly once and to
 * suppress the normal right-edge exit while the boss is still alive
 * (otherwise the player could skip the fight by walking off-screen).
 *
 * Keep this list explicit — wiring a new boss is two lines (add the
 * entityType + the corresponding case in `playSceneSpawns.ts`) and
 * relying on an implicit "anything tagged Boss" convention has bitten
 * other repos before. The cost of the explicit set is negligible.
 *
 * @module bossArena
 */

import { NOVA_WIDTH } from '../../constants/gameConstants';
import { gameState, type Enemy, type NovaRuntime } from './gameState';

/**
 * Stable `entityType` tags for every defeatable arena boss. When a
 * new boss is added, append its tag here and the boss-clear pipeline
 * picks it up with no other changes.
 */
export const BOSS_ENTITY_TYPES: ReadonlySet<string> = new Set([
  'KingRivet',
  'PrismLeviathan',
  'StarEaterBoss',
]);

/**
 * Return the first boss enemy in the current scene, or `null` if the
 * current level is not a boss arena.
 *
 * The lookup is a linear scan because boss arenas only ever hold a
 * single boss instance plus a handful of decorative enemies (none
 * today). Adding an indexed cache would be premature.
 *
 * @returns The boss `Enemy` if one is present, else `null`.
 */
export function getActiveBoss(): Enemy | null {
  // DECISION: For-loop over Array.find — physics step hot path, no
  // allocator pressure from a closure, identical behaviour.
  for (const e of gameState.enemies) {
    if (BOSS_ENTITY_TYPES.has(e.entityType)) return e;
  }
  return null;
}

/**
 * Apply the level-end exit rules to Nova's current position, mutating
 * `nova` and `gameState.levelComplete` as needed.
 *
 * Cases, in order:
 * 1. Boss arena, boss in array, `dead === true` → freeze Nova, set
 *    `levelComplete`, return `true`. Reachable from tests that mutate
 *    `boss.dead` directly; combatStep splices dead enemies in-place so
 *    this branch rarely fires in real gameplay.
 * 2. Boss arena, boss in array, alive → clamp Nova at the right edge
 *    so she can't walk past the fight; return `false`.
 * 3. No boss in array, `gameState.bossDefeated === true` → freeze Nova
 *    and set `levelComplete`. This is the real-game post-combat path:
 *    combatStep raises the flag the same tick it splices the boss, and
 *    physicsStep calls this function again after combat so the level
 *    clears even though the boss is no longer in the array.
 * 4. No boss + not in finale + Nova at right edge → standard exit:
 *    freeze Nova and set `levelComplete`.
 *
 * @param nova         Player runtime — mutated when exit conditions fire.
 * @param levelWidth   Logical-pixel width of the current level.
 * @param finaleActive `true` while the W1 finale cutscene is running;
 *                     suppresses the standard right-edge exit so the
 *                     finale beat can drive `levelComplete` itself.
 * @returns `true` when `gameState.levelComplete` was just set and the
 *          caller should bail; `false` to continue the physics step.
 */
export function applyLevelExitRules(
  nova: NovaRuntime,
  levelWidth: number,
  finaleActive: boolean,
): boolean {
  const boss = getActiveBoss();
  if (boss !== null) {
    if (boss.dead) {
      nova.vx = 0;
      nova.vy = 0;
      nova.state = 'IDLE';
      gameState.levelComplete = true;
      return true;
    }
    if (nova.x > levelWidth - NOVA_WIDTH) {
      nova.x = levelWidth - NOVA_WIDTH;
      nova.vx = 0;
    }
    return false;
  }
  if (gameState.bossDefeated && !gameState.levelComplete) {
    nova.vx = 0;
    nova.vy = 0;
    nova.state = 'IDLE';
    gameState.levelComplete = true;
    return true;
  }
  if (!finaleActive && nova.x >= levelWidth - NOVA_WIDTH) {
    nova.x = levelWidth - NOVA_WIDTH;
    nova.vx = 0;
    nova.vy = 0;
    nova.state = 'IDLE';
    gameState.levelComplete = true;
    return true;
  }
  return false;
}
